import { supabase } from "../lib/supabase";
import { mediaFields, uploadItems, type Slot, type SlotKey } from "../lib/reviewMedia";
import { forgetUploads, runUploads, undoUploads } from "../lib/mediaUpload";
import { reviewRemove, reviewUpload } from "../components/ReviewMedia";
import { weekOk } from "../lib/reviewerWeek";
import type { WeekTree } from "../lib/reviewerWeekVocabulary.generated";
import type { Suggestion } from "../lib/reviewerSeat";

/* THE REVIEWER SEAT'S WRITES (step 2, 2026-10-10) — HIS OWN ROWS AND NOTHING ELSE.
 *
 *   • `reviewer_reviews` + `reviewer_review_items` — a day's review: his words, a comment per exercise, takes (voice ·
 *     video · photo in `coach-review-media` `<his uid>/<row id>.m4a|mp4|jpg`), SUGGESTED next-week numbers per set.
 *     A draft until Send; Send sets `sent_at` (the server's clock) and the review is final (the guards refuse a change).
 *   • `reviewer_week_proposals` — a WHOLE proposed week (`week` = the edited copy of `reviewer_week`'s tree), checked
 *     against the one vocabulary BEFORE the write (`weekOk`), the same walk as the database's CHECK.
 *
 * 🔴 NO `coach_*` TABLE, NO TRAINING TABLE: the ruler `reviewerSeatNoCoachWrite.test.ts` reads this file and every seat
 * file and fails on any `.from("coach_…")`, any `.from(` outside the three, and any RPC outside the seat's own. */

export type ReviewItemDraft = {
  /** The performed row (`workout_session_exercises.id`) — the guard holds it to the reviewed session; null = none. */
  wseId: string | null;
  exerciseName: string;
  body: string;
  suggested: Suggestion[];
  /** The page's media slot for this exercise (the composer's). */
  slot: SlotKey;
};
export type ReviewDraft = {
  reviewerId: string; clientId: string; sessionId: string; scheduledDate: string;
  /** The existing draft's id, or null for a first save. */
  reviewId: string | null;
  body: string;
  overallSlot: SlotKey;
  items: ReviewItemDraft[];
};

const clip = (s: string, n: number) => [...s.trim()].slice(0, n).join("");
const textOrNull = (s: string, n: number) => (s.trim() === "" ? null : clip(s, n));

/** Saves the draft — words and suggestions, NO takes (a take lives in this tab until Send, like the coach's).
 *  Returns the review's id or the refusal. Items are replaced whole (a draft's items are his to rewrite). */
export async function saveReviewDraft(d: ReviewDraft): Promise<{ id: string; error: null } | { id: null; error: string }> {
  const id = d.reviewId ?? crypto.randomUUID();
  if (d.reviewId === null) {
    const ins = await supabase.from("reviewer_reviews").insert({ id, client_id: d.clientId, session_id: d.sessionId, scheduled_date: d.scheduledDate, body: textOrNull(d.body, 4000) });
    if (ins.error !== null) return { id: null, error: ins.error.message };
  } else {
    const up = await supabase.from("reviewer_reviews").update({ body: textOrNull(d.body, 4000) }).eq("id", id);
    if (up.error !== null) return { id: null, error: up.error.message };
  }
  const del = await supabase.from("reviewer_review_items").delete().eq("review_id", id);
  if (del.error !== null) return { id: null, error: del.error.message };
  const rows = d.items.filter((i) => i.body.trim() !== "" || i.suggested.length > 0).map((i, n) => ({
    review_id: id, workout_session_exercise_id: i.wseId, position: n, exercise_name: clip(i.exerciseName, 80),
    body: textOrNull(i.body, 2000), suggested: i.suggested.length === 0 ? null : i.suggested,
  }));
  if (rows.length > 0) {
    const it = await supabase.from("reviewer_review_items").insert(rows);
    if (it.error !== null) return { id: null, error: it.error.message };
  }
  return { id, error: null };
}

/** Send: the takes upload FIRST (one ordered queue), then the rows are written with their paths, then `sent_at`. Any
 *  failure removes what landed; a review inserted by this send is deleted again (a draft may be — the policy allows it). */
export async function sendReview(d: ReviewDraft, slots: Readonly<Record<SlotKey, Slot>>, blobOf: (ref: string) => Blob | undefined): Promise<string | null> {
  const id = d.reviewId ?? crypto.randomUUID();
  const kept = d.items.map((i) => ({ ...i, id: crypto.randomUUID() }))
    .filter((i) => i.body.trim() !== "" || i.suggested.length > 0 || slots[i.slot] !== undefined && Object.keys(slots[i.slot] ?? {}).length > 0);
  const items = uploadItems(d.reviewerId, [{ slot: d.overallSlot, id }, ...kept.map((i) => ({ slot: i.slot, id: i.id }))], slots);
  const up = await runUploads(items, blobOf, reviewUpload, reviewRemove);
  if (up.error !== null) return `Not sent: a recording did not upload (${up.error}). Nothing was sent — send again.`;
  const undo = async (msg: string, inserted: boolean) => {
    await undoUploads(up.landed, reviewRemove);
    if (inserted) await supabase.from("reviewer_reviews").delete().eq("id", id);
    return `Not sent: ${msg}`;
  };
  const head = { body: textOrNull(d.body, 4000), ...mediaFields(d.reviewerId, id, slots[d.overallSlot]) };
  const fresh = d.reviewId === null;
  const r1 = fresh
    ? await supabase.from("reviewer_reviews").insert({ id, client_id: d.clientId, session_id: d.sessionId, scheduled_date: d.scheduledDate, ...head })
    : await supabase.from("reviewer_reviews").update(head).eq("id", id);
  if (r1.error !== null) return undo(r1.error.message, false);
  const del = await supabase.from("reviewer_review_items").delete().eq("review_id", id);
  if (del.error !== null) return undo(del.error.message, fresh);
  if (kept.length > 0) {
    const r2 = await supabase.from("reviewer_review_items").insert(kept.map((i, n) => ({
      id: i.id, review_id: id, workout_session_exercise_id: i.wseId, position: n, exercise_name: clip(i.exerciseName, 80),
      body: textOrNull(i.body, 2000), suggested: i.suggested.length === 0 ? null : i.suggested, ...mediaFields(d.reviewerId, i.id, slots[i.slot]),
    })));
    if (r2.error !== null) return undo(r2.error.message, fresh);
  }
  // The server stamps its own clock (the guard); the value sent only says "now".
  const r3 = await supabase.from("reviewer_reviews").update({ sent_at: new Date().toISOString() }).eq("id", id).select("id");
  if (r3.error !== null) return undo(r3.error.message, fresh);
  if ((r3.data ?? []).length !== 1) return undo("the review was not marked sent", fresh);
  forgetUploads(up.landed);
  return null;
}

/** The proposed week: insert or update the ONE row for (him, client, Monday); `send` sets `sent_at` (final). The tree is
 *  checked against the vocabulary first — a week the database would refuse is never sent to it. */
export async function saveProposal(p: { clientId: string; monday: string; proposalId: string | null; week: WeekTree; note: string; send: boolean }):
  Promise<{ id: string; error: null } | { id: null; error: string }> {
  if (!weekOk(p.week, p.monday)) return { id: null, error: "This week has a value the page cannot save (a name left empty, or too much)." };
  const note = textOrNull(p.note, 4000);
  const sent = p.send ? { sent_at: new Date().toISOString() } : {};
  if (p.proposalId === null) {
    const id = crypto.randomUUID();
    const r = await supabase.from("reviewer_week_proposals").insert({ id, client_id: p.clientId, week_start: p.monday, week: p.week as never, note, ...sent });
    return r.error === null ? { id, error: null } : { id: null, error: r.error.message };
  }
  const r = await supabase.from("reviewer_week_proposals").update({ week: p.week as never, note, ...sent }).eq("id", p.proposalId).select("id");
  if (r.error !== null) return { id: null, error: r.error.message };
  if ((r.data ?? []).length !== 1) return { id: null, error: "The draft could not be found — it may already be sent." };
  return { id: p.proposalId, error: null };
}
